/**
 * Write the project's viewport decision where `readViewportPolicy` looks.
 *
 * `viewportIsPinnedToSource` has been honoured by the preview since the policy
 * landed, but nothing could declare the policy it reads — `manifest.studio
 * .viewportPolicy` had no writer, so every project fell to the absent-policy
 * default and stayed pinned. A rule the product enforces and nobody can set is
 * the enforced-but-unpublished shape this repo tracks by name.
 *
 * The manifest is saved whole (see `viewport-policy.ts` on why it lives there
 * and why this deployment accepts a read-modify-write without a clobber
 * guard), so this returns a NEW manifest carrying every sibling it was given.
 * Replacing `studio` wholesale, or dropping a key beside it, would destroy
 * project state that nothing else holds.
 */
import type { ViewportPolicy } from './viewport-policy'

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

export function manifestWithViewportPolicy(
  manifest: unknown,
  policy: ViewportPolicy,
): Record<string, unknown> {
  const current = readRecord(manifest)
  return {
    ...current,
    studio: {
      // `studio: null` is reachable — manifests are hand-written and imported —
      // and must neither throw nor quietly discard the decision.
      ...readRecord(current.studio),
      viewportPolicy: policy,
    },
  }
}
